import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface InfoBadgeProps {
  icon: LucideIcon;
  label: string;
  value: string;
  /**
   * Visual tone:
   * - `info` (default) — informational pill, ocean-tinted
   * - `warning` — high-risk indicator (e.g. flood Zone A/V), coral-tinted
   * - `muted` — data not available, dimmed foam
   */
  tone?: 'info' | 'warning' | 'muted';
  className?: string;
}

const TONE_CLASSES: Record<NonNullable<InfoBadgeProps['tone']>, string> = {
  info: 'bg-ocean-800/60 border-foam-50/10 text-foam-50',
  warning: 'bg-coral-500/15 border-coral-500/40 text-coral-100',
  muted: 'bg-foam-50/5 border-foam-50/10 text-foam-50/50',
};

const TONE_ICON_CLASSES: Record<NonNullable<InfoBadgeProps['tone']>, string> = {
  info: 'text-foam-50/70',
  warning: 'text-coral-100',
  muted: 'text-foam-50/40',
};

const TONE_VALUE_CLASSES: Record<NonNullable<InfoBadgeProps['tone']>, string> = {
  info: 'text-foam-50/80',
  warning: 'text-coral-100',
  muted: 'text-foam-50/50',
};

/**
 * `<InfoBadge>` — pill component for informational data NOT in the composite
 * score. Used for flood zone status + top internet provider on the detail
 * page. Per UI-SPEC § 3.4.
 *
 * Three tones: `info` (default), `warning` (high-risk flood), `muted`
 * (data missing — renders "Not available" copy).
 *
 * ARIA: `role="status" aria-label="{label}: {value}"`. Icon is decorative
 * (`aria-hidden`) — the label carries semantics.
 */
export function InfoBadge({
  icon: Icon,
  label,
  value,
  tone = 'info',
  className,
}: InfoBadgeProps) {
  return (
    <div
      role="status"
      aria-label={`${label}: ${value}`}
      className={cn(
        'inline-flex items-center gap-2 h-10 px-3 rounded-full border text-sm',
        TONE_CLASSES[tone],
        className,
      )}
    >
      <Icon aria-hidden className={cn('w-4 h-4', TONE_ICON_CLASSES[tone])} />
      <span className="font-semibold">{label}</span>
      <span aria-hidden className="text-foam-50/30">·</span>
      <span className={TONE_VALUE_CLASSES[tone]}>{value}</span>
    </div>
  );
}
